import React, { createContext, useContext, useState, ReactNode } from "react";
import { WineReplacementReason } from "@tc/ui-dfe/query";
import { ReasonDialogContextType, ReasonDialogProviderProps, ReasonDialogResult } from "./types";
import { ReasonDialog } from "./ReasonDialog";

const ReasonDialogContext = createContext<ReasonDialogContextType>({
    openDialog: () => Promise.resolve({ confirmed: false, reason: "", reasonCategories: [] }),
});

export const ReasonDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [modal, setModal] = useState<ReasonDialogProviderProps & { open: boolean }>({
        open: false,
        title: "",
        message: null,
    });
    const [reason, setReason] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [selectedReasons, setSelectedReasons] = useState<WineReplacementReason[]>([]);
    const [reasonsError, setReasonsError] = useState<string>("");
    const [resolvePromise, setResolvePromise] = useState<(result: ReasonDialogResult) => void>(() => () => { });

    const openDialog = (options: ReasonDialogProviderProps): Promise<ReasonDialogResult> => {
        return new Promise((resolve) => {
            setModal({ ...options, open: true });
            setResolvePromise(() => resolve);
        });
    };

    const closeDialog = () => {
        setModal({ open: false, title: "", message: null });
        setReason("");
        setError("");
        setSelectedReasons([]);
        setReasonsError("");
    };

    const handleConfirm = () => {
        if (modal.reasonsRequired && selectedReasons.length === 0) {
            setReasonsError("Please select at least one reason.");
            return;
        }
        // Free-text note is optional when a reason select is present (Story 179791).
        const hasReasonSelect = !!modal.reasonOptions && modal.reasonOptions.length > 0;
        if (!hasReasonSelect && modal.required && !reason?.trim()) {
            setError("This field is required.");
            return;
        }
        // Only enforce max length when a note has actually been entered.
        if (modal.maxLength && reason && reason.length > modal.maxLength) {
            setError(`Maximum length is ${modal.maxLength} characters.`);
            return;
        }
        resolvePromise({ confirmed: true, reason, reasonCategories: selectedReasons });
        closeDialog();
    };

    const handleCancel = () => {
        resolvePromise({ confirmed: false, reason: "", reasonCategories: [] });
        closeDialog();
    };

    return (
        <ReasonDialogContext.Provider value={{ openDialog }}>
            {children}
            <ReasonDialog
                {...modal}
                cancelDialog={handleCancel}
                handleConfirm={handleConfirm}
                setError={setError}
                setReason={setReason}
                error={error}
                selectedReasons={selectedReasons}
                setSelectedReasons={setSelectedReasons}
                reasonsError={reasonsError}
                setReasonsError={setReasonsError}
            ></ReasonDialog>
        </ReasonDialogContext.Provider>
    );
};

export const useReasonDialog = (): ReasonDialogContextType => {
    return useContext(ReasonDialogContext);
};
